import { Banknote } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatAmount } from '../money';
import type { Report } from '../reports/types';
import { listPayments } from './api';
import { formatDay } from './format';
import { leftToPay, PaymentDialog } from './PaymentDialog';
import type { Payment } from './types';

function PaymentList({ payments }: { readonly payments: readonly Payment[] }) {
  const { t } = useTranslation('closing');
  return (
    <ul className="flex flex-col gap-1 text-sm" aria-label={t('payments.title')}>
      {payments.map((payment) => (
        <li key={payment.id} className="flex flex-wrap gap-x-2">
          <span className="tabular-nums">
            {t('payments.line', {
              date: formatDay(payment.paidOn),
              amount: formatAmount(payment.amount),
              reference: payment.reference,
            })}
          </span>
          <span className="text-muted-foreground">
            {t('payments.by', { name: payment.recordedBy.displayName })}
            {payment.note ? ` — «${payment.note}»` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Under the totals (spec §1.10): «Rimborsato X di Y» with the list of payments once there are
 * any, and «Registra rimborso» for whoever `can.recordPayment`. Nothing shows on a report that
 * has no payment and cannot take one.
 */
export function PaymentsSection({
  report,
  adopt,
}: {
  readonly report: Report;
  readonly adopt: (report: Report) => void;
}) {
  const { t } = useTranslation('closing');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [recording, setRecording] = useState(false);
  const paid = Number(report.reimbursed) > 0;
  const may = report.can?.recordPayment === true;
  const id = report.id;
  // Set when a payment is recorded here: it is added to the list, not read again.
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    if (!paid || loaded.current === id) return;
    let active = true;
    void listPayments(id).then((answer) => {
      if (active && answer.ok) setPayments(answer.value.items ?? []);
    });
    return () => {
      active = false;
    };
  }, [id, paid]);

  if (!paid && !may) return null;
  const showButton = may && leftToPay(report) > 0;
  return (
    <section
      aria-label={t('payments.title')}
      className="border-border flex flex-col gap-2 border-t pt-3"
      data-testid="report-payments"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {paid
            ? t('payments.summary', {
                paid: formatAmount(report.reimbursed),
                of: formatAmount(report.reimbursable),
              })
            : t('payments.title')}
        </h3>
        {showButton ? (
          <Button type="button" variant="outline" onClick={() => setRecording(true)}>
            <Banknote />
            {t('payments.register')}
          </Button>
        ) : null}
      </div>
      {paid ? (
        <PaymentList payments={payments} />
      ) : (
        <p className="text-muted-foreground text-sm">{t('payments.none')}</p>
      )}
      {recording ? (
        <PaymentDialog
          report={report}
          onClose={() => setRecording(false)}
          onRecorded={(payment, next) => {
            loaded.current = id;
            setPayments((current) => [...current, payment]);
            adopt(next);
            setRecording(false);
          }}
        />
      ) : null}
    </section>
  );
}
